import type { MRT_ColumnDef } from "mantine-react-table-open";
import { getRowId } from "../hooks/usePeriodBasedTable";
import type {
  ExternalErrors,
  PeriodBasedRow,
  ValidationErrors,
  ValueCellProps,
  ValueEditProps
} from "../PeriodBasedTable.types";

interface ValueColumnProps<
  T extends Record<string, unknown> = Record<string, unknown>
> {
  header: string;
  fieldName: string;
  CellComponent: React.ComponentType<ValueCellProps<T>>;
  EditComponent: React.ComponentType<ValueEditProps<T>>;
  creationErrors?: ValidationErrors;
  editingErrors?: ValidationErrors;
  externalErrors?: ExternalErrors;
}

export function getValueColumn<
  T extends Record<string, unknown> = Record<string, unknown>
>({
  header,
  fieldName,
  CellComponent,
  EditComponent,
  creationErrors,
  editingErrors,
  externalErrors
}: ValueColumnProps<T>): MRT_ColumnDef<PeriodBasedRow<T>> {
  return {
    header,
    accessorKey: fieldName,
    size: 50,
    enableSorting: false,
    Edit: ({ row, table }) => {
      const currentValue =
        (row._valuesCache as Record<string, unknown>)[fieldName] ??
        (row.original as Record<string, unknown>)[fieldName];
      const isCreating = table.getState().creatingRow?.id === row.id;
      const isEditing = table.getState().editingRow?.id === row.id;

      const getErrorText = () => {
        if (isCreating || isEditing) {
          return isCreating ? creationErrors?.value : editingErrors?.value;
        }

        const rowId = getRowId(row.original);
        return externalErrors?.[rowId]?.[fieldName];
      };

      return (
        <EditComponent
          error={getErrorText()}
          row={row.original}
          value={currentValue as string | number}
          onChange={(newValue) => {
            (row._valuesCache as Record<string, unknown>)[fieldName] = newValue;
            const { editingRow, creatingRow } = table.getState();
            if (creatingRow?.id === row.id) {
              table.setCreatingRow(row);
            } else if (editingRow?.id === row.id) {
              table.setEditingRow(row);
            }
          }}
        />
      );
    },
    Cell: ({ row }) => {
      const rowId = getRowId(row.original);
      const externalError = externalErrors?.[rowId]?.[fieldName];
      return <CellComponent error={externalError} row={row.original} />;
    }
  };
}
